import { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Play, Pencil, Save, Plus, Trash2, X, Mic, Boxes, Bot, GraduationCap, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { iconFor } from "@/lib/icons";
import { api } from "@/lib/api";
import { Simulator } from "@/components/Simulator";
import { VoiceCoach } from "@/components/VoiceCoach";
import { ProductCases } from "@/components/ProductCases";
import { RecoAssistant } from "@/components/RecoAssistant";
import { Academy } from "@/components/Academy";
import { CoachProgress } from "@/components/CoachProgress";

export const ToolboxPanel = () => {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("vente");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const [edit, setEdit] = useState(false);
  const [sim, setSim] = useState(null);
  const [coachCase, setCoachCase] = useState(null);

  useEffect(() => { api.toolbox().then(setData).catch(() => {}); }, []);

  const modes = useMemo(() => {
    const src = (data?.modes || []).filter((m) => m.id !== "exercices");
    const reseau = src.find((m) => m.id === "reseau");
    const energie = src.find((m) => m.id === "energie");
    const rest = src.filter((m) => m.id !== "reseau" && m.id !== "energie");
    if (!reseau && !energie) return rest;
    const merged = {
      id: "methode", icon: "compass", title: "Méthode & routine",
      subtitle: "Présence, réseau, énergie et planning — l'essentiel réuni",
      sections: [...(reseau?.sections || []), ...(energie?.sections || [])], virtual: true,
    };
    return [...rest, merged];
  }, [data]);
  const current = modes.find((m) => m.id === mode) || modes[0];
  const simulations = data?.simulations || [];

  const sections = useMemo(() => {
    if (!current) return [];
    const term = q.trim().toLowerCase();
    if (!term) return current.sections;
    return current.sections
      .map((s) => ({ ...s, items: s.items.filter((it) => `${s.title} ${it.t} ${it.st} ${it.body}`.toLowerCase().includes(term)) }))
      .filter((s) => s.items.length);
  }, [current, q]);

  const mutate = (fn) => {
    const copy = JSON.parse(JSON.stringify(data));
    const m = copy.modes.find((x) => x.id === current.id);
    fn(m);
    setData(copy);
  };
  const save = async () => {
    try { await api.updateToolbox(data.modes); toast.success("Boîte à outils enregistrée."); setEdit(false); }
    catch { toast.error("Échec de l'enregistrement."); }
  };

  if (!data) return <div className="text-slate-400 text-sm py-10 text-center">Chargement de la boîte à outils…</div>;

  return (
    <div data-testid="toolbox-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {modes.map((m) => {
          const Icon = iconFor(m.icon);
          const active = m.id === current?.id;
          return (
            <button key={m.id} data-testid={`toolbox-mode-${m.id}`} onClick={() => { setMode(m.id); setQ(""); setOpen({}); setEdit(false); }}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${active ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
              <Icon size={16} className={active ? "text-[#C89A3E]" : ""} /> {m.title}
            </button>
          );
        })}
        <button data-testid="toolbox-mode-coach" onClick={() => { setMode("coach"); setQ(""); setOpen({}); setEdit(false); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${mode === "coach" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <Mic size={16} className={mode === "coach" ? "text-[#C89A3E]" : ""} /> Entraîneur vocal IA
        </button>
        <button data-testid="toolbox-mode-progress" onClick={() => { setMode("progress"); setQ(""); setOpen({}); setEdit(false); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${mode === "progress" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <TrendingUp size={16} className={mode === "progress" ? "text-[#C89A3E]" : ""} /> Ma progression
        </button>
        <button data-testid="toolbox-mode-cases" onClick={() => { setMode("cases"); setQ(""); setOpen({}); setEdit(false); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${mode === "cases" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <Boxes size={16} className={mode === "cases" ? "text-[#C89A3E]" : ""} /> Produits &amp; cas
        </button>
        <button data-testid="toolbox-mode-reco" onClick={() => { setMode("reco"); setQ(""); setOpen({}); setEdit(false); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${mode === "reco" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <Bot size={16} className={mode === "reco" ? "text-[#C89A3E]" : ""} /> Assistant IA
        </button>
        <button data-testid="toolbox-mode-academy" onClick={() => { setMode("academy"); setQ(""); setOpen({}); setEdit(false); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${mode === "academy" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <GraduationCap size={16} className={mode === "academy" ? "text-[#C89A3E]" : ""} /> Académie
        </button>
      </div>

      {mode === "cases" && <ProductCases onTrain={(c) => { setCoachCase(c); setMode("coach"); }} />}
      {mode === "reco" && <RecoAssistant onTrain={(c) => { setCoachCase(c); setMode("coach"); }} />}
      {mode === "academy" && <Academy />}
      {mode === "progress" && <CoachProgress />}
      {mode === "coach" && <VoiceCoach initialCase={coachCase} onCaseConsumed={() => setCoachCase(null)} />}

      {mode !== "coach" && mode !== "cases" && mode !== "reco" && mode !== "academy" && mode !== "progress" && (<>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm text-slate-500">{current?.subtitle}</p>
        {current?.id !== "exercices" && !current?.virtual && (
          edit ? (
            <div className="flex gap-2">
              <button data-testid="toolbox-save" onClick={save} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#012141] text-white text-xs font-semibold"><Save size={13} /> Enregistrer</button>
              <button onClick={() => setEdit(false)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#012141]/15 text-xs font-semibold text-[#012141]"><X size={13} /> Fermer</button>
            </div>
          ) : (
            <button data-testid="toolbox-edit" onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#012141]/15 text-xs font-semibold text-[#012141] hover:border-[#C89A3E]"><Pencil size={13} /> Éditer</button>
          )
        )}
      </div>

      {current?.id !== "exercices" && (
        <div className="relative mb-5">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="toolbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer par mot-clé…" className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#012141]/15 bg-white text-sm focus:outline-none focus:border-[#C89A3E]" />
        </div>
      )}

      {/* S'exercer : simulateur */}
      {current?.id === "exercices" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {simulations.map((sc) => (
            <button key={sc.id} data-testid={`sim-launch-${sc.id}`} onClick={() => setSim(sc)} className="text-left rounded-2xl border border-[#012141]/12 bg-white p-4 hover:border-[#C89A3E] hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#012141] text-sm">{sc.title}</span>
                <span className="grid place-items-center w-8 h-8 rounded-full bg-[#012141] text-[#C89A3E]"><Play size={14} /></span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{sc.persona}</p>
            </button>
          ))}
        </div>
      )}

      {/* Autres modes : contenu (+ édition) */}
      {current?.id !== "exercices" && (
        <div className="space-y-6">
          {sections.map((s, si) => (
            <div key={si}>
              <h3 className="font-heading text-lg font-bold text-[#012141] mb-3">{s.title}</h3>
              <div className="space-y-2">
                {s.items.map((it, ii) => {
                  const key = `${current.id}-${si}-${ii}`;
                  const isOpen = !!open[key] || !!q.trim() || edit;
                  return (
                    <div key={ii} className="rounded-2xl border border-[#012141]/10 overflow-hidden bg-white">
                      {edit ? (
                        <div className="p-3 space-y-2">
                          <input value={it.t} onChange={(e) => mutate((m) => { m.sections[si].items[ii].t = e.target.value; })} className="w-full text-sm font-semibold text-[#012141] border border-[#012141]/15 rounded-lg px-2 py-1.5" placeholder="Titre" />
                          <input value={it.st} onChange={(e) => mutate((m) => { m.sections[si].items[ii].st = e.target.value; })} className="w-full text-xs text-slate-500 border border-[#012141]/15 rounded-lg px-2 py-1.5" placeholder="Sous-titre" />
                          <textarea value={it.body} onChange={(e) => mutate((m) => { m.sections[si].items[ii].body = e.target.value; })} rows={3} className="w-full text-sm text-slate-600 border border-[#012141]/15 rounded-lg px-2 py-1.5" placeholder="Contenu" />
                          <button data-testid={`item-delete-${si}-${ii}`} onClick={() => mutate((m) => { m.sections[si].items.splice(ii, 1); })} className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold"><Trash2 size={12} /> Supprimer</button>
                        </div>
                      ) : (
                        <>
                          <button data-testid={`toolbox-item-${current.id}-${si}-${ii}`} onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))} className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-[#012141]/4">
                            <span>
                              <span className="block font-semibold text-[#012141] text-sm">{it.t}</span>
                              {it.st && <span className="block text-xs text-slate-500 mt-0.5">{it.st}</span>}
                            </span>
                            <ChevronDown size={16} className={`flex-shrink-0 mt-1 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </button>
                          {isOpen && it.body && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line border-t border-[#012141]/5 pt-3">{it.body}</div>}
                        </>
                      )}
                    </div>
                  );
                })}
                {edit && (
                  <button data-testid={`item-add-${si}`} onClick={() => mutate((m) => { m.sections[si].items.push({ t: "Nouveau", st: "", body: "" }); })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#012141] px-3 py-2 rounded-full border border-dashed border-[#012141]/30"><Plus size={13} /> Ajouter un élément</button>
                )}
              </div>
            </div>
          ))}
          {sections.length === 0 && <p className="text-slate-400 text-sm py-8 text-center">Aucun résultat — essayez un autre mot-clé.</p>}
        </div>
      )}
      </>)}

      {sim && <Simulator scenario={sim} onClose={() => setSim(null)} />}
    </div>
  );
};
